/**
 * InterviewQuestionsGuide.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Guía de preguntas recomendadas por puesto — tab independiente del drawer.
 * Usa el mismo resolvedor de template que InterviewChecklist.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { useMemo } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { getTemplate } from "./InterviewChecklist";

// ─────────────────────────────────────────────────────────────────────────────
const InterviewQuestionsGuide = ({ candidato }) => {
  const template = useMemo(
    () => getTemplate(candidato?.puesto_interes || ""),
    [candidato?.puesto_interes],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, p: 2 }}>

      {/* ── Cabecera del template ── */}
      <Box sx={{
        p: 2, borderRadius: "14px",
        background: `linear-gradient(135deg, ${template.color}18, transparent)`,
        border: `1px solid ${template.color}30`,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "1.6rem", lineHeight: 1 }}>{template.icon}</Typography>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 700, color: template.color, lineHeight: 1.3 }}>
              {template.titulo}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              {template.preguntas.length} categorías · referencia durante la entrevista
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Consejo de uso ── */}
      <Box sx={{
        px: 2, py: 1.4, borderRadius: "10px",
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.07)",
      }}>
        <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
          💡 Usa estas preguntas como guía. Adapta el lenguaje y la profundidad según el perfil
          del candidato. No es necesario cubrir todas; elige las más relevantes para cada sesión.
        </Typography>
      </Box>

      {/* ── Categorías de preguntas ── */}
      {template.preguntas.map((cat, ci) => (
        <Box key={ci}>
          {/* Encabezado de categoría */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1.2 }}>
            <Typography sx={{ fontSize: "1rem", lineHeight: 1 }}>{cat.emoji}</Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700, textTransform: "uppercase",
                letterSpacing: "0.07em", color: template.color,
              }}
            >
              {cat.categoria}
            </Typography>
          </Box>

          {/* Lista de preguntas */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.9, pl: 1.5 }}>
            {cat.items.map((q, qi) => (
              <Box key={qi} sx={{ display: "flex", gap: 1.2, alignItems: "flex-start" }}>
                <Typography
                  sx={{ color: template.color, fontSize: "0.5rem", mt: 0.7, flexShrink: 0 }}
                >
                  ◆
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.82rem", color: "text.secondary", lineHeight: 1.65 }}
                >
                  {q}
                </Typography>
              </Box>
            ))}
          </Box>

          {ci < template.preguntas.length - 1 && (
            <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mt: 1.8 }} />
          )}
        </Box>
      ))}

    </Box>
  );
};

export default InterviewQuestionsGuide;
